import React, { useEffect, useState } from 'react';
import { Loader2, RotateCw, Undo2, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { api } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const STATUS = { offen: 'offen', angenommen: 'Bogen eingegangen', abgelaufen: 'abgelaufen', zurueckgezogen: 'zurückgezogen' };
const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');
const fehler = (e) => e?.response?.data?.detail || 'Aktion fehlgeschlagen';
const inp = 'w-full h-10 px-3 rounded-[10px] border border-gray-200 focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#e0b56a]/40 outline-none text-sm';

export const EinladungZeile = ({ e, onChanged }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => { setBusy(true); try { await fn(); toast({ title: msg }); onChanged(); } catch (x) { toast({ title: fehler(x), variant: 'destructive' }); } finally { setBusy(false); } };
  const link = `${window.location.origin}/mandantenerfassung?einladung=${e.code}`;
  return (
    <li className="flex items-center gap-2 py-2 text-xs" data-testid={`einladung-${e.id}`}>
      <span className="flex-1 min-w-0"><b className="text-[#1B3A5C]">{e.name}</b> <span className="text-gray-500">{e.email || e.telefon}</span>
        <span className="block text-[10px] text-gray-500">Code {e.code} · {STATUS[e.status]}{e.status === 'offen' ? ` bis ${datum(e.gueltig_bis)}` : ''}{e.erinnert_am ? ` · erinnert am ${datum(e.erinnert_am)}` : ''}</span></span>
      {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {e.status !== 'angenommen' && e.status !== 'zurueckgezogen' && <>
        <button type="button" title="Link kopieren" onClick={() => { navigator.clipboard?.writeText(link); toast({ title: 'Link kopiert' }); }} className="p-1.5 hover:text-[#b8893d]" data-testid={`einladung-kopieren-${e.id}`}><Copy className="w-3.5 h-3.5" /></button>
        <button type="button" title="Erneut senden" onClick={() => run(() => api.post(`/kanzlei/erfassung-einladungen/${e.id}/erneut`), 'Einladung erneut gesendet')} className="p-1.5 hover:text-[#b8893d]" data-testid={`einladung-erneut-${e.id}`}><RotateCw className="w-3.5 h-3.5" /></button>
      </>}
      {e.status === 'offen' && <button type="button" title="Zurückziehen" onClick={() => window.confirm('Einladung zurückziehen?') && run(() => api.delete(`/kanzlei/erfassung-einladungen/${e.id}`), 'Einladung zurückgezogen')} className="p-1.5 hover:text-rose-600" data-testid={`einladung-zurueck-${e.id}`}><Undo2 className="w-3.5 h-3.5" /></button>}
    </li>
  );
};

export const ErfassungEinladenDialog = ({ onClose }) => {
  const { toast } = useToast();
  const [f, setF] = useState({ name: '', email: '', telefon: '' });
  const [busy, setBusy] = useState(false);
  const [liste, setListe] = useState(null);
  const laden = () => api.get('/kanzlei/erfassung-einladungen').then((r) => setListe(r.data.items)).catch(() => setListe([]));
  useEffect(() => { laden(); }, []);
  const senden = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const r = await api.post('/kanzlei/erfassung-einladungen', { name: f.name, email: f.email || null, telefon: f.telefon || null });
      toast({ title: r.data.gesendet_am ? 'Einladung gesendet' : 'Einladung angelegt', description: r.data.gesendet_am ? `Code ${r.data.code}` : `Code ${r.data.code} – bitte Link manuell weitergeben` });
      setF({ name: '', email: '', telefon: '' }); laden();
    } catch (x) { toast({ title: fehler(x), variant: 'destructive' }); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="einladung-dialog">
        <DialogTitle className="font-serif text-2xl text-[#1B3A5C]">Zur Mandantenerfassung einladen</DialogTitle>
        <DialogDescription className="text-xs">Persönlicher Link mit Code, 14 Tage gültig. Die E-Mail enthält keine Angaben zur Angelegenheit.</DialogDescription>
        <form onSubmit={senden} className="space-y-3" data-testid="einladung-form">
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className={inp} data-testid="einladung-name" />
          <div className="grid sm:grid-cols-2 gap-3">
            <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="E-Mail" className={inp} data-testid="einladung-email" />
            <input value={f.telefon} onChange={(e) => setF({ ...f, telefon: e.target.value })} placeholder="oder Mobilnummer" className={inp} data-testid="einladung-telefon" />
          </div>
          <button type="submit" disabled={busy || !f.name || (!f.email && !f.telefon)} data-testid="einladung-senden"
            className="w-full h-10 rounded-[10px] bg-[#1B3A5C] text-white text-sm hover:bg-[#2A5280] disabled:opacity-50 transition-colors">{busy ? 'Wird gesendet …' : 'Einladung senden'}</button>
        </form>
        <div className="mt-2">
          <p className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-1">Letzte Einladungen</p>
          {!liste ? <Loader2 className="w-4 h-4 animate-spin" /> : !liste.length ? <p className="text-xs text-gray-500">Noch keine Einladungen.</p>
            : <ul className="divide-y divide-gray-100 max-h-56 overflow-y-auto" data-testid="einladung-liste">{liste.map((e) => <EinladungZeile key={e.id} e={e} onChanged={laden} />)}</ul>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ErfassungEinladenDialog;
